import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import BrandMark from '../components/BrandMark';
import LangSwitch from '../components/LangSwitch';
import { api } from '../lib/api';
import { useLicense } from '../lib/license';
import { useT } from '../lib/i18n';

export default function Redeem() {
  const { t } = useT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useLicense();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Set once we've kicked off the magic-link auto-activation, so a parent
  // re-render can't double-fire the request.
  const autoSubmittedRef = useRef(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await api.post('/license/activate', { code: code.trim() });
      await refresh();
      navigate('/');
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      if (detail.includes('Unknown')) setError(t('redeem.errorUnknown'));
      else if (detail.includes('already in use')) setError(t('redeem.errorFull'));
      else setError(t('redeem.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  // Magic-link flow: if the URL contains ?code=…, pre-fill the input and
  // auto-activate. The user sees a single, calm "activating" beat instead of
  // having to type anything. Errors fall back to the manual form so the user
  // can still try again or paste a different code.
  useEffect(() => {
    if (autoSubmittedRef.current) return;
    const fromUrl = searchParams.get('code');
    if (!fromUrl) return;
    const cleaned = fromUrl.trim().toUpperCase();
    if (!cleaned) return;
    autoSubmittedRef.current = true;
    setCode(cleaned);

    (async () => {
      setSubmitting(true);
      setError('');
      try {
        await api.post('/license/activate', { code: cleaned });
        await refresh();
        navigate('/');
      } catch (err) {
        const detail = err?.response?.data?.detail || '';
        if (detail.includes('Unknown')) setError(t('redeem.errorUnknown'));
        else if (detail.includes('already in use')) setError(t('redeem.errorFull'));
        else setError(t('redeem.errorGeneric'));
      } finally {
        setSubmitting(false);
      }
    })();
  }, [searchParams, navigate, refresh, t]);

  return (
    <div className="min-h-screen flex flex-col fade-in font-pressura" data-testid="redeem-screen">
      <header className="w-full px-4 sm:px-10 lg:px-16 pt-6 sm:pt-10 lg:pt-12 pb-4 grid grid-cols-3 items-center gap-2">
        <div className="justify-self-start">
          <Link
            to="/"
            className="text-[0.6rem] sm:text-xs tracking-[0.12em] sm:tracking-[0.18em] uppercase text-[#000000] hover:opacity-60 transition-opacity duration-300 font-pressura"
            style={{ fontWeight: 700 }}
            data-testid="redeem-back-link"
          >
            {t('nav.back')}
          </Link>
        </div>
        <div className="justify-self-center" aria-hidden="true" />
        <div className="justify-self-end" />
      </header>

      <main className="flex-1 flex flex-col items-center pt-2 pb-24 px-4">
        <Link to="/" className="block hover:opacity-90 transition-opacity duration-300" aria-label="Connect to Love — home">
          <BrandMark size="hero" />
        </Link>

        <form onSubmit={submit} className="w-full max-w-md mt-12 sm:mt-16 space-y-8 text-center">
          <div>
            <p
              className="font-pressura font-bold text-[#000000] leading-snug uppercase tracking-[0.04em]"
              style={{ fontSize: 'clamp(1.0625rem, 2vw, 1.5rem)' }}
              data-testid="redeem-headline"
            >
              {t('redeem.headline')}
            </p>
            <p className="font-pressura text-sm text-[#000000] opacity-70 mt-3 leading-relaxed">
              {t('redeem.helper')}
            </p>
          </div>

          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            className="ctl-input font-pressura text-center tracking-[0.18em] text-lg"
            placeholder="CTL-XXXX-XXXX"
            autoFocus
            autoComplete="off"
            spellCheck="false"
            data-testid="redeem-input"
          />

          <button
            type="submit"
            disabled={!code.trim() || submitting}
            className="ctl-shimmer-copper bg-transparent border-0 p-0 cursor-pointer font-pressura font-bold tracking-[0.18em] uppercase hover:opacity-80 transition-opacity duration-300 disabled:opacity-40"
            style={{ fontSize: 'clamp(1rem, 1.6vw, 1.25rem)', textDecoration: 'underline', textUnderlineOffset: '0.4em', textDecorationThickness: '2px' }}
            data-testid="redeem-submit-btn"
          >
            {submitting ? t('redeem.activating') : t('redeem.activate')}
          </button>

          {error && (
            <p className="font-pressura text-sm text-[#8B2A18]" data-testid="redeem-error">
              {error}
            </p>
          )}

          <p className="font-pressura text-xs opacity-60 pt-6">
            {t('redeem.noLicense')}{' '}
            <Link to="/buy" className="underline underline-offset-4 hover:opacity-100">
              {t('redeem.getOne')}
            </Link>
          </p>
        </form>
      </main>

      <footer className="w-full pb-10 flex items-center justify-center" data-testid="redeem-footer">
        <LangSwitch />
      </footer>
    </div>
  );
}
